import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useParams } from "react-router";
import { Card, CardContent, Heading, Paragraph, Skeleton } from "@ir/ui";
import { PageContent } from "@src/presentation/components/Layout/common";
import { useMemo, type ReactNode } from "react";
import { useOrganizationsData } from "@src/presentation/providers/OrganizationsDataProvider";
import { useOrganizationRouteFetch } from "./utils/organizationRouteFetch";
import {
  BrandCard,
  OrganizationDetailsPageHeader,
  OrganizationDetailsStats,
  OrganizationDetailsTwoColumnLayout,
  useUpdateBrandStatus,
  type BrandWithFeatures,
} from "./utils";
import {
  LoadingSpinner,
  type BreadcrumbItem,
} from "@src/presentation/components/common";

export const OrganizationDetailsPage = () => {
  const { organizationKey } = useParams<{ organizationKey: string }>();

  useOrganizationRouteFetch(organizationKey);

  const {
    organizationDetails,
    organizationDetailsLoading,
    organizationDetailsError,
    setOrganizationDetails,
    setCachedDetails,
    applyBrandCreate,
  } = useOrganizationsData();

  const handleUpdateBrandStatus = useUpdateBrandStatus();

  const organizationName = organizationDetails
    ? (organizationDetails.organizationDisplayName ??
      organizationDetails.organizationName ??
      "")
    : "";
  const orgKey =
    organizationDetails?.organizationKey ?? organizationKey ?? "";
  const orgDescription = organizationDetails?.organizationDescription ?? null;
  const brands = (organizationDetails?.brands ?? []) as BrandWithFeatures[];

  const headerSubtitle = (() => {
    if (organizationDetailsLoading) return "Loading organization details...";
    if (organizationDetailsError) return "Organization details error";
    return "Organization Settings";
  })();

  const showInvalid = useMemo(
    () => !organizationKey && !organizationDetailsLoading,
    [organizationKey, organizationDetailsLoading],
  );

  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const root: BreadcrumbItem = {
      key: "organizations-root",
      title: "Organizations & Brands",
      path: "/organizations",
    };
    if (!organizationKey) {
      return [root];
    }
    if (organizationDetailsLoading) {
      return [
        root,
        {
          key: "organization-current",
          title: (
            <Skeleton
              height={16}
              width={200}
              animation="wave"
              variant="rounded"
            />
          ),
        },
      ];
    }
    return [
      root,
      {
        key: "organization-current",
        title: organizationName || organizationKey || "Organization",
      },
    ];
  }, [organizationKey, organizationDetailsLoading, organizationName]);

  const brandsSection: ReactNode = (() => {
    if (brands.length > 0) {
      return (
        <OrganizationDetailsTwoColumnLayout>
          {brands.map((brand) => (
            <BrandCard
              key={brand.brandId}
              brand={brand}
              organizationKey={organizationKey}
              organizationId={organizationDetails?.organizationId}
              onUpdateBrandStatus={handleUpdateBrandStatus}
              onUpdateSuccess={() => {}}
            />
          ))}
        </OrganizationDetailsTwoColumnLayout>
      );
    }
    return (
      <Card className="bg-white mt-2" variant="outlined" shadow>
        <CardContent>
          <Paragraph className="text-grey-500 font-regular m-0">
            No brands for this organization.
          </Paragraph>
        </CardContent>
      </Card>
    );
  })();

  const pageContentBody: ReactNode = (() => {
    if (organizationDetailsLoading) return <LoadingSpinner />;
    if (organizationDetailsError && !organizationDetails) return null;
    return (
      <>
        <OrganizationDetailsStats
          organizationName={organizationName}
          organizationKey={orgKey}
          brandCount={brands.length}
        />

        {orgDescription && (
          <Card className="bg-white mb-3" variant="outlined" shadow>
            <CardContent>
              <Paragraph className="text-grey-500 font-regular m-0 mb-1">
                Description
              </Paragraph>
              <Paragraph className="text-grey-700 m-0">
                {orgDescription}
              </Paragraph>
            </CardContent>
          </Card>
        )}

        <Heading level={5} className="text-grey-700 font-bold m-0">
          Brands
        </Heading>
        {brandsSection}
      </>
    );
  })();

  return (
    <ErrorBoundary>
      <OrganizationDetailsPageHeader
        organization={organizationDetails}
        headerTitle={organizationName}
        subtitle={headerSubtitle}
        error={showInvalid ? "Invalid organization" : organizationDetailsError}
        organizationDetailsLoading={organizationDetailsLoading}
        breadcrumbItems={breadcrumbItems}
        onOrganizationSaved={(saved) => {
          if (organizationKey) {
            setCachedDetails(organizationKey, saved);
          }
          setOrganizationDetails(saved);
        }}
        onBrandSaved={(saved) => {
          if (organizationKey) {
            applyBrandCreate(organizationKey, saved);
          }
        }}
      />
      <PageContent className="p-3">
        {organizationKey ? (
          pageContentBody
        ) : (
          <Paragraph className="text-grey-500">Invalid organization.</Paragraph>
        )}
      </PageContent>
    </ErrorBoundary>
  );
};
